import { ReactNode, useEffect, useMemo, useState } from "react";
import { ConfirmChallenge } from "src/components/confirm-challenge/confirm-challenge";
import { Text } from "@ucl/react-library";
import { Trans, useTranslation } from "react-i18next";
import { CaseFormTypes, OfferStatus } from "../case-landing/case-form-setup";
import { mapCustomerUpdateFormData } from "src/utils/map-customer-update-data";
import { updateCustomerDetails } from "src/api/services/customer-service";
import { useCase } from "src/context/case-context";
import { ContactDetailsSection } from "src/components/contact-details/contact-details-section";
import { useMultiStepForm } from "src/hooks/use-multi-step-form";
import { MultiStepFormLayout } from "src/components/multi-step-form-layout/multi-step-form-layout";
import { useNavigate } from "react-router-dom";
import { Loader } from "src/utils/loader";
import { translationMapping } from "src/utils/translation-mappings";

type StepConfig = {
  step: number;
  body: ReactNode;
};

export const ConfirmChallengePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerData, setCustomerData } = useCase();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    showEditDetails,
    setShowEditDetails,
    showError,
    setShowError,
    methods,
    isAgreed,
    currentStep,
    totalSteps,
    handleContinue,
    handleBack,
  } = useMultiStepForm({
    initialSections: ["contactDetails", "preferredContactChannel", "challengeDecision", "agreeTsAndCs"],
  });

  const challengeDecision = methods.watch("challengeDecision");

  const showDontWantToChallenge = currentStep === totalSteps && challengeDecision === "no-challenge";

  const handleContactOnClick = () => {
    setShowEditDetails(!showEditDetails);
    window.scrollTo(0, 0);
  };

  const contactErrors = methods.formState.errors;

  useEffect(() => {
    if (contactErrors?.address || contactErrors?.preferredContactChannel) {
      setShowEditDetails(true);
    }
  }, [setShowEditDetails, setShowError, contactErrors?.preferredContactChannel, contactErrors?.address]);

  const steps = useMemo<StepConfig[]>(
    () => [
      {
        step: 1,
        body: (
          <div data-testid="challengeContactDetails" className="flex flex-col gap-10">
            <div className="flex flex-col gap-4">
              <Text renderAs="h2" styleType="h4" fontWeight="semi-bold">
                {t("confirmChallenge.step1Title")}
              </Text>

              <Text.P size="large">{t("confirmChallenge.step1Para1")}</Text.P>

              <Text.P size="large">{t("confirmChallenge.step1Para2")}</Text.P>

              <Text.P size="large">
                <Trans i18nKey="confirmChallenge.step1Para3" components={translationMapping(t)} />
              </Text.P>
            </div>

            <ContactDetailsSection
              showContactPreference={true}
              showContactExplainer={true}
              showEditDetails={showEditDetails}
              toggleContactDetails={handleContactOnClick}
            />
          </div>
        ),
      },
      {
        step: 2,
        body: <ConfirmChallenge />,
      },
    ],
    [showEditDetails, setShowEditDetails, t]
  );

  if (!customerData) return null;

  const onSubmit = async (formData: CaseFormTypes) => {
    if (challengeDecision === "no-challenge") {
      navigate("/case");
      return;
    }

    setShowError(false);
    setIsSubmitting(true);

    try {
      const mappedData = mapCustomerUpdateFormData({
        ...formData,
        offerStatus: OfferStatus.IntentToObject,
      });

      const updatedData = await updateCustomerDetails(mappedData);
      setCustomerData(updatedData);

      navigate("/thank-you", {
        state: { pageType: "challengeSubmitted" },
      });
    } catch {
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MultiStepFormLayout
        methods={methods}
        onSubmit={onSubmit}
        currentStep={currentStep}
        totalSteps={totalSteps}
        steps={steps}
        showError={showError}
        onCloseError={() => setShowError(false)}
        errorTitleKey="errors.updateGeneric.title"
        errorBodyKey="errors.updateGeneric.body"
        submitLabelKey="confirmChallenge.submitChallenge"
        continueLabelKey="confirmChallenge.continueToChallenge"
        submitDisabled={challengeDecision !== "challenge" || !isAgreed}
        handleContinue={handleContinue}
        handleBack={handleBack}
        hideActions={showDontWantToChallenge}
      />
      {isSubmitting && <Loader />}
    </>

  );
};
